import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Copy, RefreshCw, Link as LinkIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { createShare, revokeShare, regenerateShare, lookupShare } from "@/api";

/**
 * Share toggle + Regenerate UI. `type` is "project" | "moodboard", `targetId` is the entity id.
 */
export default function ShareDialog({ open, onOpenChange, type, targetId, label }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const publicUrl = share
    ? `${window.location.origin}/shared/${share.id}`
    : "";

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await lookupShare(type, targetId);
        if (active) setShare(res?.share || null);
      } catch {
        if (active) setShare(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, type, targetId]);

  async function toggle(on) {
    setBusy(true);
    try {
      if (on) {
        const created = await createShare({ type, target_id: targetId });
        setShare(created);
        toast.success("Share link enabled");
      } else if (share) {
        await revokeShare(share.id);
        setShare(null);
        toast.success("Share link revoked");
      }
    } catch {
      toast.error("Could not update share");
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    if (!share) return;
    setBusy(true);
    try {
      const next = await regenerateShare(share.id);
      setShare(next);
      toast.success("New token generated — old link no longer works");
    } catch {
      toast.error("Could not regenerate");
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <LinkIcon className="w-5 h-5" strokeWidth={1.5} />
            Share {label || type}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-stone-400">
            <Loader2 className="w-5 h-5 animate-spin inline-block" />
          </div>
        ) : (
          <div className="space-y-5 pt-2">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-stone-900">Public link</p>
                <p className="text-xs text-stone-500">
                  Anyone with the link can view a read-only copy.
                </p>
              </div>
              <Switch
                checked={!!share}
                onCheckedChange={toggle}
                disabled={busy}
                data-testid="share-toggle"
              />
            </div>

            {share && (
              <>
                <div className="flex items-center gap-2">
                  <Input
                    value={publicUrl}
                    readOnly
                    className="rounded-full bg-stone-50 text-stone-700 text-sm"
                    data-testid="share-url-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    onClick={copy}
                    variant="outline"
                    className="rounded-full shrink-0"
                    data-testid="share-copy-button"
                  >
                    <Copy className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
                <div className="flex items-center justify-between text-xs text-stone-500">
                  <span>Token: …{share.id.slice(-8)}</span>
                  <div className="flex items-center gap-2">
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="share-open-link"
                      className="inline-flex items-center gap-1 text-stone-700 hover:text-stone-900"
                    >
                      Open <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                    </a>
                    <Button
                      onClick={regenerate}
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      data-testid="share-regenerate-button"
                      className="rounded-full"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" strokeWidth={1.5} />
                      Regenerate
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
